/* eslint-disable */
/**
 * Theme Helper Utilities
 *
 * Provides utility functions for:
 * - Getting nested color values by path
 * - Generating CSS variables from colors
 * - Color manipulation
 *
 * TODO: Change anys to proper types and remove eslint disable
 */

import type { VennColors } from '../../style/color';

/**
 * Get a nested value from an object using dot-notation path.
 *
 * @param obj The object to traverse
 * @param path Dot-notation path (e.g., 'Primary.Main', 'HoldingsColor.SectorColor.Cyclical')
 * @returns The value at the path, or undefined if not found
 *
 * @example
 * ```ts
 * const colors = { Primary: { Main: '#00CCCC' } };
 * getNestedValue(colors, 'Primary.Main'); // '#00CCCC'
 * getNestedValue(colors, 'Primary.Missing'); // undefined
 * ```
 */
export const getNestedValue = (obj: any, path: string): any => {
  if (!path) return undefined;

  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
};

/**
 * Get all color paths in a nested color object.
 * Returns array of dot-notation paths to all color values.
 *
 * @param colors The colors object to traverse
 * @param prefix Optional prefix for nested paths
 * @returns Array of paths to all color values
 *
 * @example
 * ```ts
 * const colors = {
 *   Primary: { Main: '#00CCCC', Light: '#99FFFF' },
 *   DarkBlue: '#0072AF'
 * };
 * getAllColorPaths(colors);
 * // Returns: ['Primary.Main', 'Primary.Light', 'DarkBlue']
 * ```
 */
export const getAllColorPaths = (colors: any, prefix = ''): string[] => {
  const paths: string[] = [];

  for (const key in colors) {
    if (Object.prototype.hasOwnProperty.call(colors, key)) {
      const value = colors[key];
      const fullPath = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'string' && value.startsWith('#')) {
        // This is a color value
        paths.push(fullPath);
      } else if (typeof value === 'object' && value !== null) {
        // Recursively get nested paths
        paths.push(...getAllColorPaths(value, fullPath));
      }
    }
  }

  return paths;
};

/**
 * Generate CSS custom properties (variables) from a colors object.
 * Useful for using theme colors in CSS.
 *
 * @param colors The colors object
 * @param prefix Optional prefix for variable names (default: 'theme')
 * @returns Object with CSS variable declarations
 *
 * @example
 * ```ts
 * const colors = {
 *   Primary: { Main: '#00CCCC' },
 *   DarkBlue: '#0072AF'
 * };
 * const vars = generateCSSVariables(colors, 'app');
 * // Returns: {
 * //   '--app-Primary-Main': '#00CCCC',
 * //   '--app-DarkBlue': '#0072AF'
 * // }
 *
 * // Use in CSS:
 * // .myDiv { color: var(--app-Primary-Main); }
 * ```
 */
export const generateCSSVariables = (colors: any, prefix = 'theme'): Record<string, string> => {
  const variables: Record<string, string> = {};
  const paths = getAllColorPaths(colors);

  for (const path of paths) {
    const value = getNestedValue(colors, path);
    if (typeof value === 'string') {
      const varName = `--${prefix}-${path.replace(/\./g, '-')}`;
      variables[varName] = value;
    }
  }

  return variables;
};

/**
 * Convert RGB values to hex color.
 * Helper for generatePalette.
 *
 * @param r Red value (0-255)
 * @param g Green value (0-255)
 * @param b Blue value (0-255)
 * @returns Hex color string
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b]
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    })
    .join('')
    .toUpperCase()}`;
};

/**
 * Create a style object with CSS variable fallbacks.
 * Useful for components that need both inline styles and CSS variable support.
 *
 * @param colorPath Path to the color (e.g., 'Primary.Main')
 * @param colors The colors object
 * @param varPrefix CSS variable prefix
 * @returns Object suitable for style prop
 *
 * @example
 * ```ts
 * const colors = useColors();
 * const style = createColorStyle('Primary.Main', colors, 'app');
 * // Returns: { color: '#00CCCC' }
 * ```
 */
export const createColorStyle = (colorPath: string, colors: VennColors, varPrefix = 'theme'): React.CSSProperties => {
  const value = getNestedValue(colors, colorPath);
  const varName = `var(--${varPrefix}-${colorPath.replace(/\./g, '-')})`;

  if (!value || typeof value !== 'string') {
    return {};
  }

  return {
    color: value,
  } as React.CSSProperties;
};
